import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Separator } from '@/components/ui/separator'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Server,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Save,
  Loader2,
  CheckCircle,
  XCircle,
  Shield,
  Globe,
  Terminal,
  Code
} from 'lucide-react'
import { getAllServers, createServer, updateServer, deleteServer } from '@/services/api'

// Helper functions for default commands (must be defined before component)
function getDefaultEmailCommand(type) {
  switch (type) {
    case 'PLESK':
      return "plesk bin mail --create '{email}' -passwd '{password}' -mailbox true -mbox_quota {quota}"
    case 'CPANEL':
      return "uapi --output=json Email add_pop email='{localPart}' password='{password}' domain='{domain}' quota={quota}"
    default:
      return ''
  }
}

function getDefaultPasswordCommand(type) {
  switch (type) {
    case 'PLESK':
      return "sshpass -p '{serverPassword}' ssh -o StrictHostKeyChecking=no -o ConnectTimeout=10 -p {sshPort} {sshUser}@{sshHost} 'plesk bin mail -u \"{email}\" -passwd \"{password}\"'"
    case 'CPANEL':
      return "sshpass -p '{serverPassword}' ssh -o StrictHostKeyChecking=no -o LogLevel=ERROR -o ConnectTimeout=10 -p {sshPort} {sshUser}@{sshHost} 'uapi --output=json Email passwd_pop email=\"{email}\" password=\"{password}\" 2>&1'"
    default:
      return ''
  }
}

function getDefaultImapsyncCommand() {
  return "imapsync --host1 '{sourceHost}' --user1 '{email}' --password1 '{password}' --host2 '{destHost}' --user2 '{email}' --password2 '{password}' --ssl1 --ssl2 --noauthmd5 --fastskip --errorsmax {errorsMax} --timeout {timeout} --regextrans2 's/^INBOX\\\\.(.+)/$1/' --skipheader \"Content-Length\" --noreleasecheck"
}

export default function Servers() {
  const [servers, setServers] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedServer, setSelectedServer] = useState(null)
  const [saving, setSaving] = useState(false)
  
  // Alert modal state
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'error' // 'error' | 'success'
  })
  
  const [formData, setFormData] = useState(() => {
    const defaultType = 'CPANEL'
    return {
      name: '',
      host: '',
      type: defaultType,
      port: 993,
      ssl: true,
      username: '',
      password: '',
      apiToken: '',
      useApiToken: false,
      sshEnabled: false,
      sshPort: 22,
      useCustomCommands: false,
      emailCreationCommand: getDefaultEmailCommand(defaultType),
      passwordUpdateCommand: getDefaultPasswordCommand(defaultType),
      imapsyncCommand: getDefaultImapsyncCommand()
    }
  })

  useEffect(() => {
    loadServers()
  }, [])

  // Helper to show alert modal
  const showAlert = (title, message, type = 'error') => {
    setAlertModal({ isOpen: true, title, message, type })
  }

  const loadServers = async () => {
    setLoading(true)
    console.log('=== LOADING SERVERS ===')
    try {
      const data = await getAllServers()
      console.log('Servers loaded:', data.length)
      if (data.length > 0) {
        console.log('First server ID:', data[0].id)
        console.log('First server keys:', Object.keys(data[0]))
      }
      setServers(data)
    } catch (err) {
      console.error('Error loading servers:', err)
      showAlert('Error', 'Error loading servers: ' + (err.message || err), 'error')
    }
    setLoading(false)
  }

  const handleAdd = async () => {
    console.log('handleAdd called with formData:', formData)
    setSaving(true)
    try {
      // If not using custom commands, clear them before sending
      const dataToSend = {
        ...formData,
        emailCreationCommand: formData.useCustomCommands ? formData.emailCreationCommand : null,
        passwordUpdateCommand: formData.useCustomCommands ? formData.passwordUpdateCommand : null,
        imapsyncCommand: formData.useCustomCommands ? formData.imapsyncCommand : null
      }
      console.log('Data being sent to API:', dataToSend)
      const result = await createServer(dataToSend)
      console.log('Server created successfully:', result)
      setIsAddDialogOpen(false)
      resetForm()
      loadServers()
    } catch (err) {
      console.error('Error creating server:', err)
      console.error('Error response:', err.response)
      showAlert('Error', 'Error creating server: ' + (err.response?.data?.message || err.message), 'error')
    }
    setSaving(false)
  }

  const handleEdit = async () => {
    if (!selectedServer) {
      showAlert('Error', 'No server selected!', 'error')
      return
    }
    
    // Debug: Check if we have a valid server ID
    console.log('SELECTED SERVER:', selectedServer)
    console.log('SELECTED SERVER ID:', selectedServer?.id)
    
    if (!selectedServer.id) {
      showAlert('Error', 'Server ID is missing!', 'error')
      console.error('Server object missing ID:', selectedServer)
      return
    }
    
    setSaving(true)
    
    // Build the payload exactly as the API expects
    const dataToSend = {
      name: formData.name,
      host: formData.host,
      type: formData.type,
      port: formData.port,
      ssl: formData.ssl,
      username: formData.username,
      password: formData.password || null,  // Include password if provided
      apiToken: formData.apiToken || null,  // Include token if provided (blank = keep existing)
      useApiToken: formData.useApiToken || false,
      sshEnabled: formData.sshEnabled,
      sshPort: formData.sshPort,
      useCustomCommands: formData.useCustomCommands,
      emailCreationCommand: formData.useCustomCommands ? formData.emailCreationCommand : null,
      passwordUpdateCommand: formData.useCustomCommands ? formData.passwordUpdateCommand : null,
      imapsyncCommand: formData.useCustomCommands ? formData.imapsyncCommand : null
    }
    
    console.log('=== UPDATE REQUEST ===')
    console.log('URL:', `/api/servers/${selectedServer.id}`)
    console.log('Payload:', JSON.stringify(dataToSend, null, 2))
    
    try {
      console.log('Calling updateServer...')
      const result = await updateServer(selectedServer.id, dataToSend)
      console.log('=== UPDATE SUCCESS ===')
      console.log('Response:', result)
      showAlert('Success', 'Server updated successfully!', 'success')
      setIsEditDialogOpen(false)
      setSelectedServer(null)
      resetForm()
      await loadServers()
    } catch (err) {
      console.error('=== UPDATE FAILED ===')
      console.error('Error:', err)
      console.error('Error message:', err.message)
      console.error('Error response:', err.response)
      
      // Try with direct fetch as fallback
      console.log('Trying direct fetch...')
      try {
        const fetchResponse = await fetch(`/api/servers/${selectedServer.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataToSend)
        })
        console.log('Fetch response status:', fetchResponse.status)
        const fetchData = await fetchResponse.json()
        console.log('Fetch response data:', fetchData)
        if (fetchResponse.ok) {
          showAlert('Success', 'Server updated via fetch!', 'success')
          setIsEditDialogOpen(false)
          setSelectedServer(null)
          resetForm()
          await loadServers()
        } else {
          showAlert('Error', 'Fetch also failed: ' + fetchData.message, 'error')
        }
      } catch (fetchErr) {
        console.error('Fetch also failed:', fetchErr)
        showAlert('Error', 'Error updating server: ' + (err.message || err), 'error')
      }
    }
    setSaving(false)
  }

  const handleDelete = async () => {
    if (!selectedServer) return
    setSaving(true)
    try {
      await deleteServer(selectedServer.id)
      setIsDeleteDialogOpen(false)
      setSelectedServer(null)
      loadServers()
    } catch (err) {
      console.error('Error deleting server:', err)
    }
    setSaving(false)
  }

  const openEditDialog = (server) => {
    console.log('=== OPEN EDIT DIALOG ===')
    console.log('Server received:', server)
    console.log('Server ID:', server?.id)
    console.log('Server ID type:', typeof server?.id)
    
    if (!server || !server.id) {
      console.error('Invalid server object:', server)
      showAlert('Error', 'Server data is missing ID!', 'error')
      return
    }
    
    setSelectedServer(server)
    const useCustom = server.useCustomCommands || false
    const newFormData = {
      name: server.name,
      host: server.host,
      type: server.type,
      port: server.port,
      ssl: server.ssl,
      username: server.username || '',
      password: '',
      apiToken: '',
      useApiToken: server.useApiToken || false,
      sshPort: server.sshPort || 22,
      sshEnabled: server.sshEnabled || false,
      useCustomCommands: useCustom,
      emailCreationCommand: useCustom ? (server.emailCreationCommand || '') : getDefaultEmailCommand(server.type),
      passwordUpdateCommand: useCustom ? (server.passwordUpdateCommand || '') : getDefaultPasswordCommand(server.type),
      imapsyncCommand: useCustom ? (server.imapsyncCommand || '') : getDefaultImapsyncCommand()
    }
    console.log('Form data set to:', newFormData)
    setFormData(newFormData)
    setIsEditDialogOpen(true)
  }

  const openDeleteDialog = (server) => {
    setSelectedServer(server)
    setIsDeleteDialogOpen(true)
  }

  const resetForm = () => {
    const defaultType = 'CPANEL'
    setFormData({
      name: '',
      host: '',
      type: defaultType,
      port: 993,
      ssl: true,
      username: '',
      password: '',
      apiToken: '',
      useApiToken: false,
      sshEnabled: false,
      sshPort: 22,
      useCustomCommands: false,
      emailCreationCommand: getDefaultEmailCommand(defaultType),
      passwordUpdateCommand: getDefaultPasswordCommand(defaultType),
      imapsyncCommand: getDefaultImapsyncCommand()
    })
  }

  const filteredServers = servers.filter(s => 
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.host.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const getServerIcon = (type) => {
    switch (type) {
      case 'CPANEL':
        return <div className="w-8 h-8 rounded bg-orange-100 dark:bg-orange-900 flex items-center justify-center text-orange-600 dark:text-orange-400 font-bold text-xs">cP</div>
      case 'PLESK':
        return <div className="w-8 h-8 rounded bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-xs">Ps</div>
      default:
        return <Server className="w-8 h-8 text-muted-foreground" />
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground/90">Servers</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage cPanel and Plesk servers for migrations
          </p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <Button onClick={() => { resetForm(); setIsAddDialogOpen(true); }} className="shrink-0">
            <Plus className="w-4 h-4 mr-2" />
            Add Server
          </Button>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Server</DialogTitle>
              <DialogDescription>
                Add a new cPanel or Plesk server for email migrations
              </DialogDescription>
            </DialogHeader>
            <ServerForm 
              formData={formData} 
              setFormData={setFormData}
              saving={saving}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={handleAdd} disabled={saving || !formData.name || !formData.host}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Add Server
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          placeholder="Search servers…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 h-9 text-sm"
        />
      </div>

      {/* Servers Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : filteredServers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-14 h-14 rounded-2xl soft-card flex items-center justify-center mb-4 !transform-none">
            <Server className="w-6 h-6 text-primary/50" />
          </div>
          <p className="text-sm font-medium text-foreground/70">No servers found</p>
          <p className="text-xs text-muted-foreground mt-1">Add a server to get started</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filteredServers.map((server) => (
            <Card key={server.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {getServerIcon(server.type)}
                    <div>
                      <CardTitle className="text-sm font-medium text-foreground/85">{server.name}</CardTitle>
                      <CardDescription className="text-xs font-mono">{server.host}</CardDescription>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 -mr-1 -mt-1">
                        <MoreVertical className="w-3.5 h-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditDialog(server)}>
                        <Edit className="w-3.5 h-3.5 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => openDeleteDialog(server)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center flex-wrap gap-1.5 text-xs">
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Globe className="w-3 h-3" />
                    {server.ssl ? 'SSL' : 'No SSL'}
                  </span>
                  <span className="text-muted-foreground">· Port {server.port}</span>
                  <span className={`ml-auto px-1.5 py-0.5 rounded-md text-[11px] font-medium border-0 ${
                    server.type === 'CPANEL' ? 'chip-amber' : 'bg-sky-100/80 text-sky-700'
                  }`}>
                    {server.type}
                  </span>
                  {server.sshEnabled && (
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] chip-success border-0">
                      <Shield className="w-3 h-3" />
                      SSH
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Server</DialogTitle>
            <DialogDescription>
              Update server configuration
            </DialogDescription>
          </DialogHeader>
          <ServerForm 
            formData={formData} 
            setFormData={setFormData}
            saving={saving}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Server</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{selectedServer?.name}</strong>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert Modal */}
      <Dialog open={alertModal.isOpen} onOpenChange={(open) => setAlertModal({...alertModal, isOpen: open})}>
        <DialogContent className="max-w-md">
          <DialogHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                alertModal.type === 'success' ? 'bg-success/12' : 'bg-destructive/10'
              }`}>
                {alertModal.type === 'success' ? (
                  <CheckCircle className="w-4 h-4 text-success" />
                ) : (
                  <XCircle className="w-4 h-4 text-destructive" />
                )}
              </div>
              <div>
                <DialogTitle>{alertModal.title}</DialogTitle>
              </div>
            </div>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-muted-foreground">{alertModal.message}</p>
          </div>
          <DialogFooter className="pt-2">
            <Button size="sm" onClick={() => setAlertModal({...alertModal, isOpen: false})}>
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ServerForm({ formData, setFormData, saving }) {
  return (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <Label>Server Name</Label>
        <Input
          placeholder="My cPanel Server"
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          disabled={saving}
        />
      </div>
      <div className="space-y-2">
        <Label>Hostname</Label>
        <Input
          placeholder="mail.example.com"
          value={formData.host}
          onChange={(e) => setFormData({...formData, host: e.target.value})}
          disabled={saving}
        />
      </div>
      <div className="space-y-2">
        <Label>Server Type</Label>
        <select
          className="w-full h-9 px-3 rounded-lg text-sm glass-input focus:outline-none focus:ring-2 focus:ring-ring/60"
          value={formData.type}
          onChange={(e) => {
            const newType = e.target.value
            setFormData({
              ...formData, 
              type: newType,
              // Update default commands when type changes
              emailCreationCommand: formData.useCustomCommands ? (formData.emailCreationCommand || getDefaultEmailCommand(newType)) : getDefaultEmailCommand(newType),
              passwordUpdateCommand: formData.useCustomCommands ? (formData.passwordUpdateCommand || getDefaultPasswordCommand(newType)) : getDefaultPasswordCommand(newType)
            })
          }}
          disabled={saving}
        >
          <option value="CPANEL">cPanel</option>
          <option value="PLESK">Plesk</option>
          <option value="GENERIC_IMAP">Generic IMAP</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Port</Label>
          <Input
            type="number"
            value={formData.port}
            onChange={(e) => setFormData({...formData, port: parseInt(e.target.value)})}
            disabled={saving}
          />
        </div>
        <div className="space-y-2 pt-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.ssl}
              onChange={(e) => setFormData({...formData, ssl: e.target.checked})}
              disabled={saving}
            />
            <span className="text-sm">Use SSL</span>
          </label>
        </div>
      </div>
      <Separator />
      <div className="space-y-2">
        <Label>SSH / Shell Access</Label>
        <div className="flex items-start gap-3 p-3 rounded-xl bg-black/[0.03] border border-white/40">
          <input
            type="checkbox"
            id="sshEnabled"
            checked={formData.sshEnabled}
            onChange={(e) => setFormData({...formData, sshEnabled: e.target.checked})}
            disabled={saving}
            className="mt-1"
          />
          <div className="space-y-1">
            <label htmlFor="sshEnabled" className="text-sm font-medium cursor-pointer">
              Enable SSH/Root Access
            </label>
            <p className="text-xs text-muted-foreground">
              Required for automatic email creation on Plesk servers. 
              Username and password will be used for SSH connection.
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>SSH Username</Label>
          <Input
            placeholder="root"
            value={formData.username}
            onChange={(e) => setFormData({...formData, username: e.target.value})}
            disabled={saving}
          />
        </div>
        <div className="space-y-2">
          <Label>SSH Port</Label>
          <Input
            type="number"
            placeholder="22"
            value={formData.sshPort || 22}
            onChange={(e) => setFormData({...formData, sshPort: parseInt(e.target.value) || 22})}
            disabled={saving}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>SSH Password</Label>
        <Input
          type="password"
          placeholder="••••••••"
          value={formData.password}
          onChange={(e) => setFormData({...formData, password: e.target.value})}
          disabled={saving}
        />
      </div>

      {formData.type === 'CPANEL' && (
        <>
          <Separator />
          <div className="space-y-2">
            <Label>cPanel password reset method</Label>
            <div className="flex items-start gap-3 p-3 rounded-xl bg-black/[0.03] border border-white/40">
              <input
                type="checkbox"
                id="useApiToken"
                checked={formData.useApiToken || false}
                onChange={(e) => setFormData({...formData, useApiToken: e.target.checked})}
                disabled={saving}
                className="mt-1"
              />
              <div className="space-y-1">
                <label htmlFor="useApiToken" className="text-sm font-medium cursor-pointer">
                  Use cPanel API token instead of SSH
                </label>
                <p className="text-xs text-muted-foreground">
                  Off (default): resets run over SSH as the account (needs shell access + the SSH
                  Password above). On: use the cPanel HTTPS API with the token below (works without
                  shell). Either way, <span className="font-medium">SSH Username</span> must be the
                  cPanel account name.
                </p>
              </div>
            </div>
          </div>
          {formData.useApiToken && (
            <div className="space-y-2">
              <Label>cPanel API Token</Label>
              <Input
                type="password"
                placeholder="Paste Full Access cPanel API token (leave blank to keep existing)"
                value={formData.apiToken || ''}
                onChange={(e) => setFormData({...formData, apiToken: e.target.value})}
                disabled={saving}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                Generate a <span className="font-medium">Full Access</span> token in cPanel →
                Security → <span className="font-medium">Manage API Tokens</span>.
              </p>
            </div>
          )}
        </>
      )}

      <Separator />
      
      {/* Custom Command Templates */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Terminal className="w-4 h-4 text-muted-foreground" />
          <Label className="text-sm font-medium">Custom Command Templates</Label>
        </div>
        
        <div className="flex items-start gap-3 p-3 rounded-xl bg-black/[0.03] border border-white/40">
          <input
            type="checkbox"
            id="useCustomCommands"
            checked={formData.useCustomCommands}
            onChange={(e) => {
              const useCustom = e.target.checked
              setFormData({
                ...formData, 
                useCustomCommands: useCustom,
                // Clear commands when unchecked, fill defaults when checked
                emailCreationCommand: useCustom ? (formData.emailCreationCommand || getDefaultEmailCommand(formData.type)) : '',
                passwordUpdateCommand: useCustom ? (formData.passwordUpdateCommand || getDefaultPasswordCommand(formData.type)) : '',
                imapsyncCommand: useCustom ? (formData.imapsyncCommand || getDefaultImapsyncCommand()) : ''
              })
            }}
            disabled={saving}
            className="mt-1"
          />
          <div className="space-y-1">
            <label htmlFor="useCustomCommands" className="text-sm font-medium cursor-pointer">
              Use Custom Commands
            </label>
            <p className="text-xs text-muted-foreground">
              Override default commands with custom templates for this server.
              Available variables: {'{email}'}, {'{password}'}, {'{domain}'}, {'{localPart}'}, {'{quota}'}, {'{sshUser}'}, {'{sshHost}'}, {'{sshPort}'}, {'{serverPassword}'}
            </p>
          </div>
        </div>
        
        {formData.useCustomCommands && (
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1">
                <Code className="w-3 h-3" />
                Email Creation Command
              </Label>
              <textarea
                className="w-full min-h-[60px] px-3 py-2 text-xs rounded-lg font-mono glass-input focus:outline-none focus:ring-2 focus:ring-ring/60"
                placeholder={getDefaultEmailCommand(formData.type)}
                value={formData.emailCreationCommand}
                onChange={(e) => setFormData({...formData, emailCreationCommand: e.target.value})}
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                Default: {getDefaultEmailCommand(formData.type)}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1">
                <Code className="w-3 h-3" />
                Password Update Command
              </Label>
              <textarea
                className="w-full min-h-[60px] px-3 py-2 text-xs rounded-lg font-mono glass-input focus:outline-none focus:ring-2 focus:ring-ring/60"
                placeholder={getDefaultPasswordCommand(formData.type)}
                value={formData.passwordUpdateCommand}
                onChange={(e) => setFormData({...formData, passwordUpdateCommand: e.target.value})}
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                Default: {getDefaultPasswordCommand(formData.type)}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1">
                <Code className="w-3 h-3" />
                Imapsync Command Template
              </Label>
              <textarea
                className="w-full min-h-[80px] px-3 py-2 text-xs rounded-lg font-mono glass-input focus:outline-none focus:ring-2 focus:ring-ring/60"
                placeholder="imapsync --host1 '{sourceHost}' --user1 '{email}' --password1 '{password}' ..."
                value={formData.imapsyncCommand}
                onChange={(e) => setFormData({...formData, imapsyncCommand: e.target.value})}
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                Additional variables: {'{sourceHost}'}, {'{destHost}'}, {'{sourcePort}'}, {'{destPort}'}, {'{errorsMax}'}, {'{timeout}'}
              </p>
            </div>
          </div>
        )}
      </div>
      
    </div>
  )
}


